import { useEffect, useRef, useCallback, MutableRefObject, Dispatch } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  WebSocketMessage,
  WebSocketChunkMessage,
  WebSocketObservabilityMessage,
  WebSocketReasoningMessage,
  WebSocketCompleteMessage,
  WebSocketErrorMessage,
  WebSocketFindingAddedMessage,
  WebSocketBlockMessage,
  WebSocketEventType,
  MessageRole,
  MessageWithObservability,
  ObservabilityEvent,
  ReportBlock,
  TokenUsage,
} from '../../../types';
import { ConnectionState } from '../../../types/frontend';
import { createInvestigationService } from '../../investigation/services/investigationService';
import { WebSocketService } from '../services/websocketService';
import { useAuth } from '../../auth/context/AuthContext';
import { ChatAction, ChatState } from './useChatReducer';
import { chatMessageKeys } from './chatMessageMapping';

const WS_URL = import.meta.env.VITE_WS_URL;
if (!WS_URL) {
  throw new Error('VITE_WS_URL environment variable is required. Check your .env file.');
}

function isChunkMessage(event: WebSocketMessage): event is WebSocketChunkMessage {
  return event.type === WebSocketEventType.CHUNK && 'text' in event && typeof event.text === 'string';
}

function isObservabilityMessage(event: WebSocketMessage): event is WebSocketObservabilityMessage {
  return event.type === WebSocketEventType.OBSERVABILITY && 'event' in event;
}

function isReasoningMessage(event: WebSocketMessage): event is WebSocketReasoningMessage {
  return event.type === WebSocketEventType.REASONING && 'content' in event && typeof event.content === 'string';
}

function isErrorMessage(event: WebSocketMessage): event is WebSocketErrorMessage {
  return event.type === WebSocketEventType.ERROR && 'code' in event && 'message' in event && typeof event.message === 'string';
}

function isCompleteMessage(event: WebSocketMessage): event is WebSocketCompleteMessage {
  return event.type === WebSocketEventType.COMPLETE;
}

 
function isFindingAddedMessage(event: WebSocketMessage): event is WebSocketFindingAddedMessage {
  return event.type === WebSocketEventType.FINDING_ADDED && 'finding_id' in event;
}

function isBlockMessage(event: WebSocketMessage): event is WebSocketBlockMessage {
  return event.type === WebSocketEventType.BLOCK && 'block' in event;
}

interface UseChatWebSocketParams {
  state: ChatState;
  dispatch: Dispatch<ChatAction>;
  customerId?: string;
  wsRef: MutableRefObject<WebSocketService | null>;
  activeSessionIdRef: MutableRefObject<string | null>;
  streamingMessageRef: MutableRefObject<string>;
  streamingObservabilityRef: MutableRefObject<ObservabilityEvent[]>;
  streamingReasoningRef: MutableRefObject<string>;
  streamingBlocksRef: MutableRefObject<ReportBlock[]>;
}

export function useChatWebSocket({
  state,
  dispatch,
  customerId,
  wsRef,
  activeSessionIdRef,
  streamingMessageRef,
  streamingObservabilityRef,
  streamingReasoningRef,
  streamingBlocksRef,
}: UseChatWebSocketParams) {
  const { idToken, user, refreshCredentials } = useAuth();
  const queryClient = useQueryClient();
  const currentSessionIdRef = useRef<string | null>(null);
  const customerIdRef = useRef(customerId);
  const idTokenRef = useRef(idToken);
  const lastEventTimestampRef = useRef(state.lastEventTimestamp);
  const operationStartTimeRef = useRef(state.operationStartTime);
  const timeoutWarningRef = useRef(state.timeoutWarning);
  const reconnectGenerationRef = useRef(0);

  useEffect(() => {
    currentSessionIdRef.current = state.currentSession?.id || null;
  }, [state.currentSession?.id]);

  useEffect(() => {
    customerIdRef.current = customerId;
  }, [customerId]);

  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  useEffect(() => {
    lastEventTimestampRef.current = state.lastEventTimestamp;
  }, [state.lastEventTimestamp]);

  useEffect(() => {
    operationStartTimeRef.current = state.operationStartTime;
  }, [state.operationStartTime]);

  useEffect(() => {
    timeoutWarningRef.current = state.timeoutWarning;
  }, [state.timeoutWarning]);

  useEffect(() => {
    if (wsRef.current && idToken) {
      wsRef.current.updateToken(idToken);
    }
  }, [idToken, wsRef]);

  const handleStreamEvent = useCallback((event: WebSocketMessage) => {
    // Only reject a frame that names a *different* investigation. Several backend
    // error frames are hand-built dicts carrying no investigation_id at all
    // (EMPTY_MESSAGE, INVALID_JSON, CONNECTION_NOT_FOUND, SERVICE_UNAVAILABLE and
    // the INTERNAL_ERROR catch-all for every unhandled exception), and an
    // undefined id compared with `!==` dropped every one of them, so a backend
    // crash arrived as silence and left the spinner up for good. This socket
    // carries one session, so a frame with no id is this session's by construction.
    if (event.investigation_id && event.investigation_id !== currentSessionIdRef.current) {
      console.warn(`Ignoring event for investigation ${event.investigation_id}, current session is ${currentSessionIdRef.current}`);
      return;
    }

    switch (event.type) {
      case WebSocketEventType.CHUNK:
        if (isChunkMessage(event)) {
          streamingMessageRef.current += event.text;
          dispatch({ type: 'CHUNK_RECEIVED', text: event.text });
        }
        break;

      case WebSocketEventType.OBSERVABILITY:
        if (isObservabilityMessage(event)) {
          // The backend ships a fully-formed ObservabilityEvent (tool_start /
          // tool_complete) — store it as-is, no reconstruction.
          const obs = event.event;
          streamingObservabilityRef.current.push(obs);

          // Keep the transient "what's happening" line in sync from the event.
          const label = obs.context_summary ?? obs.message;
          const activity = obs.event_type === 'tool_complete'
            ? `${label} ${obs.status === 'error' ? '✗' : '✓'}`
            : label;
          dispatch({ type: 'THINKING', message: activity });
        }
        break;

      case WebSocketEventType.REASONING:
        if (isReasoningMessage(event)) {
          streamingReasoningRef.current += event.content;
          dispatch({ type: 'THINKING', message: 'Reasoning...' });
        }
        break;

      case WebSocketEventType.COMPLETE: {
        const finalContent = streamingMessageRef.current.trim();
        const hasBlocks = streamingBlocksRef.current.length > 0;
        // A block-only turn (show_block with no trailing text) still finalizes a
        // message — empty content carrying the blocks. Truly empty completes
        // (no text, no blocks) stay a no-op.
        if ((finalContent || hasBlocks) && isCompleteMessage(event)) {
          // The backend sends a validated TokenUsage or nothing at all. No
          // defaults are filled in here: a missing measurement must stay missing
          // so the UI can omit it rather than show an invented number.
          const tokenUsage: TokenUsage | undefined = event.usage ?? undefined;

          const accumulatedReasoning = streamingReasoningRef.current.trim();
          if (accumulatedReasoning) {
            const reasoningEvent: ObservabilityEvent = {
              event_id: `obs-reasoning-${Date.now()}`,
              timestamp: new Date().toISOString(),
              event_type: 'thinking',
              message: accumulatedReasoning,
            };
            streamingObservabilityRef.current.unshift(reasoningEvent);
          }

          const finalMessage: MessageWithObservability = {
            id: `msg-${Date.now()}`,
            content: finalContent,
            role: MessageRole.ASSISTANT,
            timestamp: new Date().toISOString(),
            investigation_id: currentSessionIdRef.current || '',
            observability: streamingObservabilityRef.current.length > 0
              ? [...streamingObservabilityRef.current]
              : undefined,
            token_usage: tokenUsage,
            blocks: hasBlocks
              ? [...streamingBlocksRef.current]
              : undefined,
          };

          // Append the finalized message to the query cache (ADR-0049) before
          // clearing the streaming placeholder, so the loaded history is preserved
          // and the completed turn replaces the live buffer without a flicker.
          // Never invalidate here — that would refetch and drop this just-streamed
          // turn if the server has not yet persisted it.
          queryClient.setQueryData<MessageWithObservability[]>(
            chatMessageKeys.list(customerIdRef.current, currentSessionIdRef.current ?? undefined),
            (old = []) => [...old, finalMessage]
          );

          dispatch({ type: 'STREAMING_COMPLETE', assistantMessage: finalMessage });

          if (currentSessionIdRef.current && customerIdRef.current && idTokenRef.current) {
            const investigationService = createInvestigationService(idTokenRef.current);
            investigationService.getInvestigationById(customerIdRef.current, currentSessionIdRef.current)
              .then(updatedInvestigation => {
                // SESSION_UPDATED carries investigation metadata only; the message
                // list lives in the query cache.
                dispatch({
                  type: 'SESSION_UPDATED',
                  session: updatedInvestigation
                });
              })
              .catch(err => console.error('Failed to refresh investigation:', err));
          }
        } else {
          dispatch({ type: 'CLEAR_STREAMING' });
        }

        streamingMessageRef.current = '';
        streamingObservabilityRef.current = [];
        streamingReasoningRef.current = '';
        streamingBlocksRef.current = [];
        break;
      }

      case WebSocketEventType.BLOCK:
        if (isBlockMessage(event)) {
          const block = event.block as ReportBlock;
          streamingBlocksRef.current.push(block);
          dispatch({
            type: 'BLOCK_ADDED',
            block,
            investigation_id: event.investigation_id,
          });
        }
        break;

      case WebSocketEventType.FINDING_ADDED:
        if (isFindingAddedMessage(event)) {
          dispatch({
            type: 'FINDING_ADDED',
            finding: {
              id: event.finding_id,
              title: event.title,
              summary: event.summary || '',
              description: event.description || '',
              rank: event.rank || 0,
              irrelevant: event.irrelevant || false,
              potential_root_cause: event.potential_root_cause || false,
              tags: event.tags || [],
            },
          });
        }
        break;

      case WebSocketEventType.ERROR:
        if (isErrorMessage(event)) {
          dispatch({ type: 'ERROR', error: event.message });
        } else {
          // An error frame we cannot read is still an error. Clearing the refs
          // without dispatching would leave status on 'streaming' forever, which
          // is the wedge this whole branch exists to end.
          console.error('Malformed error frame, failing the turn anyway:', event);
          dispatch({ type: 'ERROR', error: 'The agent reported an error.' });
        }
        streamingMessageRef.current = '';
        streamingObservabilityRef.current = [];
        streamingReasoningRef.current = '';
        streamingBlocksRef.current = [];
        break;

      default:
        // Never drop a frame in silence. An unhandled type is either a new
        // backend event or a renamed one, and because nothing logged here, a
        // whole class of terminal frame went missing without a trace.
        console.warn('Unhandled chat frame type, ignoring:', event.type, event);
        break;
    }
  }, [dispatch, streamingMessageRef, streamingObservabilityRef, streamingReasoningRef, streamingBlocksRef, queryClient]);

  useEffect(() => {
    if (!wsRef.current) {
      wsRef.current = new WebSocketService(WS_URL);

      wsRef.current.on(WebSocketEventType.CHUNK, handleStreamEvent);
      wsRef.current.on(WebSocketEventType.OBSERVABILITY, handleStreamEvent);
      wsRef.current.on(WebSocketEventType.REASONING, handleStreamEvent);
      wsRef.current.on(WebSocketEventType.COMPLETE, handleStreamEvent);
      wsRef.current.on(WebSocketEventType.ERROR, handleStreamEvent);
      wsRef.current.on(WebSocketEventType.FINDING_ADDED, handleStreamEvent);
      wsRef.current.on(WebSocketEventType.BLOCK, handleStreamEvent);

      wsRef.current.onConnectionStateChange((connected, isReconnecting) => {
        let connectionState: ConnectionState;
        let activityMessage: string | null = null;

        if (connected) {
          connectionState = 'connected';
        } else if (isReconnecting) {
          connectionState = 'reconnecting';
          activityMessage = 'Reconnecting...';
        } else {
          connectionState = 'disconnected';
          activityMessage = 'Disconnected - Check your connection';
        }

        dispatch({ type: 'CONNECTION_STATE_CHANGED', connectionState });

        if (activityMessage) {
          dispatch({ type: 'THINKING', message: activityMessage });
        }
      });

      wsRef.current.onTokenRefresh(async () => {
        try {
          await refreshCredentials();
          const newToken = sessionStorage.getItem('cognito_id_token');
          return newToken;
        } catch (error) {
          console.error('[useChat] Token refresh failed:', error);
          return null;
        }
      });
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.disconnect(true);
        wsRef.current = null;
      }
    };
  }, [wsRef, dispatch, refreshCredentials, handleStreamEvent]);

  useEffect(() => {
    if (!state.currentSession?.id || !user?.sub || !idToken) {
      return;
    }

    // Every change supersedes a switch still in flight, and only the socket's own
    // binding says whether there is nothing to do: the ref is set after a switch
    // finishes, so trusting it let an older switch land after a newer one.
    const generation = ++reconnectGenerationRef.current;
    if (wsRef.current?.isBoundTo(state.currentSession.id)) {
      activeSessionIdRef.current = state.currentSession.id;
      return;
    }

    const reconnectWebSocket = async () => {
      if (wsRef.current && activeSessionIdRef.current) {
        wsRef.current.disconnect(false);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // A newer investigation switch superseded this one during the disconnect
      // wait — abandon it so we don't clear streaming or bind to a stale session.
      if (generation !== reconnectGenerationRef.current) return;

      streamingMessageRef.current = '';
      dispatch({ type: 'CLEAR_STREAMING' });

      const newSessionId = state.currentSession?.id;
      const reconnectCustomerId = customerIdRef.current;
      if (wsRef.current && newSessionId && user.sub && reconnectCustomerId) {
        try {
          await wsRef.current.connect(newSessionId, user.sub, reconnectCustomerId, idToken);
        } catch {
          if (generation === reconnectGenerationRef.current) {
            dispatch({ type: 'ERROR', error: 'Failed to connect to investigation' });
          }
        }
      }

      if (generation === reconnectGenerationRef.current) {
        activeSessionIdRef.current = newSessionId || null;
      }
    };

    reconnectWebSocket();
  }, [state.currentSession?.id, user?.sub, idToken, wsRef, activeSessionIdRef, streamingMessageRef, dispatch]);

  useEffect(() => {
    if (state.status !== 'loading' && state.status !== 'streaming') {
      return;
    }

    const checkTimeout = () => {
      const lastEvent = lastEventTimestampRef.current;
      const opStart = operationStartTimeRef.current;
      const hasWarning = timeoutWarningRef.current;

      const timestamp = lastEvent ?? opStart;
      if (timestamp) {
        const elapsed = Date.now() - timestamp;
        if (elapsed > 60000 && !hasWarning) {
          dispatch({ type: 'TIMEOUT_WARNING', show: true });
          const elapsedSeconds = Math.floor(elapsed / 1000);
          dispatch({ type: 'THINKING', message: `Taking longer than usual... (${elapsedSeconds}s)` });
        }
      }
    };

    const interval = setInterval(checkTimeout, 5000);

    return () => clearInterval(interval);
  }, [state.status, dispatch]);
}
